import React from 'react'
import { Container, Card, Row, Col } from 'react-bootstrap'
import { FeeDetail } from './proposalElements'
import { SBText } from 'application/components/styledComponents/text'
import SpartaCustomerCount from '../estimate-compare/spartaCustomerCount'
import {
  IsSpecialEmployer,
  IsVehicleElectric,
  useWindowSize,
  capitalizeEachFirstLetter,
  formatCurrency,
  formatNumber,
} from 'application/helpers'
import { getCurrentResolutionBreakPoint } from 'application/utils/utilityFunctions'
import { ReactComponent as EVBolt } from 'application/images/ev-bolt.svg'
import { IProposalNovatedLeaseSectionProps } from './proposalInterfaces/novatedLeaseSectionProps'
import { ITheme } from 'application/utils/type'
import * as S from './proposalElements'
import * as E from './novatedLeaseSectionElements'
import * as P from './../pdf/pdfElements'
import CardEntry from './cardEntry'
import { theme as MaxxiaTheme } from 'Maxxia/theme'
import { theme as RemServTheme } from 'RemServ/theme'
import globals from 'application/constants/globals'
import { LeaseInsuranceInfoBlock } from 'application/components/styledComponents/LeaseInsuranceInfoBlock/LeaseInsuranceInfoBlock'
import { useSelector } from 'react-redux'

interface IInputProps {
  title: string
  afterMarketAccessories: any
  isProposal: boolean | undefined
  data: IProposalNovatedLeaseSectionProps
  theme: ITheme
}

const NovatedLeaseSection = (props: IInputProps) => {
  let theme = MaxxiaTheme

  if (globals.isBrandRS) theme = RemServTheme

  const extendedWarrantyTerm = props.data.extendedWarrantyTerm
  const getAddOnTerm = () => {
    return extendedWarrantyTerm && extendedWarrantyTerm > 0
      ? extendedWarrantyTerm > 1
        ? '- ' + extendedWarrantyTerm + ' years'
        : '- ' + extendedWarrantyTerm + ' year'
      : ''
  }
  const pacVal = 'pac'
  const rpcProviderlower =
    props.data.rpcProvider && props.data.rpcProvider.toLowerCase()
  const extendedWarrantyProviderlower =
    props.data.extendedWarrantyProvider &&
    props.data.extendedWarrantyProvider.toLowerCase()
  const isPacInsurer =
    rpcProviderlower === pacVal || extendedWarrantyProviderlower === pacVal
  const totalLossAssist = isPacInsurer
    ? 'Total Loss Assist Insurance'
    : 'Total Loss Assist'
  const safeGuardorExtended = isPacInsurer
    ? `Extended Warranty Insurance  ${getAddOnTerm()}`
    : 'Safeguard Ultimate Warranty'
  const extendedOneLiner = isPacInsurer
    ? 'Cover for unexpected mechanical or electrical failure to most components.'
    : `Extend the car’s factory warranty.`
  const rpcText = isPacInsurer
    ? 'Guaranteed Buy Back Insurance'
    : 'Residual Protection Cover'
  const rpcOneLiner = isPacInsurer
    ? 'Cover in most scenarios if the market value of your car is less than the residual amount at the end of your full lease term.'
    : 'Cover in case the car’s market value is less than the residual.'
  const learnMoreURL = isPacInsurer
    ? 'https://www.maxxia.com.au/insurance/novated-lease'
    : 'https://www.maxxia.com.au/insurance/insurance-policy-information'
  let leaseTerm = globals.isBrandRS
    ? props.data.leaseTerm.toString()
    : props.data.leaseTerm % 12 === 0
    ? props.data.leaseTermYears.toString() + ' Years'
    : props.data.leaseTerm.toString() + ' Months'
  let annualKmsText = formatNumber(props.data.kmPerAnnum) + ' kms'

  let vehicleType = ''
  if (props.data && props.data.vehicle && props.data.vehicle.vehicleType)
    vehicleType = props.data.vehicle.vehicleType.toLowerCase()

  let isEVRebate =
    props.data.evRebateApplicable && IsVehicleElectric(props.data, false) && vehicleType == 'new'

  const TotalAmountFinanced = () => {
    const [width] = useWindowSize()
    const resolutionBreakpoint = {
      nonDesktop: getCurrentResolutionBreakPoint(false),
    }

    let isMobile = width < resolutionBreakpoint.nonDesktop

    return isMobile ? (
      <div>
        <CardEntry
          title="Total amount financed"
          id="novatedleasesection.totalamountfinanced"
          value={formatCurrency(props.data.financeAmount)}
          boldText
          boldValue
        />
      </div>
    ) : (
      <div>
        <Row>
          <E.TotalFinanced bold lg={7}>
            Total amount financed
          </E.TotalFinanced>
          <E.TotalFinanced
            bold
            lg={5}
            className="text-right"
            id="novatedleasesection.totalamountfinanced"
          >
            {formatCurrency(props.data.financeAmount)}
          </E.TotalFinanced>
        </Row>
      </div>
    )
  }

  const InsuranceInfoSection = () => {
    let isSAGBYO = IsSpecialEmployer('SAGBYO')
    return (
      <>
      {!isSAGBYO && 
        <div className="mt-2">
          <LeaseInsuranceInfoBlock
            Included={props.data.leaseProtectionPremium > 0}
            Title="Lease Protection Insurance"
            TitleId="novatedleasesection.leaseprotectionpremiumtitle1"
            Subtext="Cover your lease repayments if you become involuntarily unemployed."
            MoreInfoUrl={learnMoreURL + '#lpi'}
            PremiumAmount={props.data.leaseProtectionPremium}
            PremiumId="novatedleasesection.leaseprotectionpremium"
            ProposalStyle
          />

          <LeaseInsuranceInfoBlock
            Included={props.data.rpcPremium > 0}
            Title={rpcText}
            TitleId="novatedleasesection.rpcpremiumtitle"
            Subtext={rpcOneLiner}
            MoreInfoUrl={learnMoreURL + '#gbbi'}
            PremiumAmount={props.data.rpcPremium}
            PremiumId="novatedleasesection.rpcpremium"
            ProposalStyle
          />

          {props.data.extendedWarrantyPremium > 0 && (
            <LeaseInsuranceInfoBlock
              Included={props.data.extendedWarrantyPremium > 0}
              Title={safeGuardorExtended}
              TitleId="novatedleasesection.extendedwarrantypremiumtitle"
              Subtext={extendedOneLiner}
              MoreInfoUrl={learnMoreURL + '#ew'}
              PremiumAmount={props.data.extendedWarrantyPremium}
              PremiumId="novatedleasesection.extendedwarrantypremium"
              ProposalStyle
            />
          )}

          <LeaseInsuranceInfoBlock
            Included={props.data.gapCoverPremium > 0}
            Title={totalLossAssist}
            TitleId="novatedleasesection.gapcoverpremiumtitle"
            Subtext="Cover in addition to your Comprehensive MV insurance payout if your
            car is written off."
            MoreInfoUrl={learnMoreURL + '#tla'}
            PremiumAmount={props.data.gapCoverPremium}
            PremiumId="novatedleasesection.gapcoverpremium"
            ProposalStyle
          />
        </div>
        }
        <hr></hr>
        <Row>
          <Col lg={2} md={0}></Col>
          <Col lg={10} md={12}>
            <TotalAmountFinanced />
            {globals.isBrandRS && (
              <Row>
                <E.TotalFinanced
                  xs={12}
                  className="font-weight-normal subtitle"
                >
                  All figures above are inclusive of GST unless stated otherwise
                </E.TotalFinanced>
              </Row>
            )}
          </Col>
        </Row>
      </>
    )
  }

  let getLeaseType = (vehicleType: string) => {
    

    if (vehicleType.includes('new')) {
      return 'New Car Lease'
    } else if (vehicleType.includes('residual')) {
      return 'Refinance Existing Lease'
    } else if (vehicleType.includes('leaseback')) {
      return 'Lifestyle Lease'
    } else if (vehicleType.includes('private')) {
      return 'Private Purchase Sale'
    } else {
      return 'Used Car Lease'
    }
  }

  const aftermarketOrderLines =
    props.afterMarketAccessories &&
    props.afterMarketAccessories.aftermarketOrderLines
  let aftermarketTotal = 0
  aftermarketOrderLines &&
    aftermarketOrderLines.forEach(
      (item: any) => (aftermarketTotal += item.priceIncGST)
    )

  const accessoriesByYou =
    props.data.vehicle &&
    props.data.vehicle.thirdPartyOption &&
    props.data.vehicle.thirdPartyOption.thirdPartyOptionLines

  const vehicleAccessories =
    props.data.vehicle && props.data.vehicle.vehicleAccessories

  let accessoryCount = 0
  accessoriesByYou && accessoriesByYou.forEach((item)=> accessoryCount += Math.floor(item.description.length/40) + 1)  
  aftermarketOrderLines && aftermarketOrderLines.forEach((item: any) => accessoryCount += Math.floor(item.description.length/40) + 1)  
    
  // Ignore dealer accessories, not listed in individual lines

  let accessoriesDesc = ''
  vehicleAccessories &&
    vehicleAccessories.forEach(
      (item) =>
        (accessoriesDesc +=
          item.name + ' at ' + formatCurrency(item.amount) + ', ')
    )
  const isDownloadingPdf = useSelector((state:any) => state.downloadPdf.isDownloading)
  const CardBody = (theme: ITheme) => {
    return (
      <Card.Body>
        {isEVRebate && props.data.pdf && (
          <SpartaCustomerCount
            line1={
              `This electric vehicle may be eligible for a ${formatCurrency(props.data.evRebateValue)} EV Bonus!`
            }
            line2={
              `The ${formatCurrency(props.data.evRebateValue)} EV Bonus is not included in any calculations below. ` +
              ('<a href=' + props.data.evRebateLink +
                ' target="_blank" rel="noopener noreferrer" class="link">' +
                'Terms &amp; Conditions Apply' +
                '</a>')
            }
            extended={false}
            icon={<EVBolt />}
            iconTopLine={true}
            spacingValue={1}
          />
        )}
        <CardEntry
          title={
            globals.isBrandMX ? 'Chosen lease term' : 'Lease term (months)'
          }
          value={leaseTerm}
          id="novatedleasesection.leaseterm"
          boldText={globals.isBrandRS}
        />
        <CardEntry
          title="Chosen lease type"
          value={getLeaseType(vehicleType)}
          id="novatedleasesection.leasetype"
          boldText={globals.isBrandRS}
        />
        <CardEntry
          title="Chosen lease product type"
          value={capitalizeEachFirstLetter(
            props.data.productId
              .toLowerCase()
              .replace('novated lease', '')
              .trim()
          )}
          id="novatedleasesection.productid"
          boldText={globals.isBrandRS}
        />
        <CardEntry
          title={
            globals.isBrandMX
              ? 'Total residual value'
              : 'Residual value (inc GST) after lease term'
          }
          value={formatCurrency(props.data.leaseResidualAmountIncGST)}
          id="novatedleasesection.leaseresidualamount"
          boldText={globals.isBrandRS}
        />
        <CardEntry
          title="Estimated annual kilometers"
          value={annualKmsText}
          id="novatedleasesection.annualkm"
          boldText={globals.isBrandRS}
        />
        <CardEntry
          title="Vehicle FBT Base Value"
          value={formatCurrency(props.data.vehicleFBTValue)}
          id="novatedleasesection.vehiclefbtvalue"
          boldText={globals.isBrandRS}
        />
        {props.data.vehicleFBTValue > 0 && (
          <S.BrandDefaultText
            className="text-smaller"
            id="novatedleasesection.vehicleFBTValuesubtext"
          >
            Used by your employer for FBT Reporting purposes (including EV
            Exempt)
          </S.BrandDefaultText>
        )}
        <div className="mt-2">
          <S.BrandDefaultText className="font-weight-bold mb-2">
            TOTAL AMOUNT FINANCED
          </S.BrandDefaultText>
          <CardEntry
            title={globals.isBrandMX ? 'Car price' : 'Vehicle purchase price'}
            id="novatedleasesection.carprice"
            value={formatCurrency(
              props.data.vehicle.invoiceTotal
                ? props.data.vehicle.invoiceTotal
                : props.data.driveawayPrice
            )}
            boldText
            boldValue={globals.isBrandRS}
            valueColour={
              globals.isBrandRS ? theme.colors.primary : theme.colors.grey2
            }
          />
          {globals.isBrandRS && (
            <S.BrandDefaultText className="text-smaller mb-3">
              Includes procurement fee of{' '}
              {formatCurrency(props.data.vehicle.procurementFee)} ex GST
            </S.BrandDefaultText>
          )}
          {props.isProposal && (
            <S.BrandDefaultText
              className="text-smaller mb-3"
              id="novatedleasesection.accessoriesdescription"
            >
              {accessoriesDesc == ''
                ? '(Based on on roads, GST on vehicle and accessories if applicable.)'
                : "(Based on dealer's accessories including " +
                  accessoriesDesc +
                  'on roads, & GST on vehicle and accessories if applicable.)'}
            </S.BrandDefaultText>
          )}
          {((aftermarketOrderLines && aftermarketOrderLines.length > 0) ||
            (vehicleAccessories &&
              vehicleAccessories.length > 0 &&
              !props.isProposal) ||
            (accessoriesByYou && accessoriesByYou.length > 0)) && (
            <div>
              <hr></hr>
              <SBText theme={theme} size={18}>
                {' '}
                ACCESSORIES{' '}
              </SBText>
              {!props.isProposal &&
                vehicleAccessories &&
                vehicleAccessories.length > 0 && (
                  <div className="mt-3">
                    <SBText theme={theme} size={18}>
                      Arranged by Dealership
                    </SBText>
                    <div className="ml-3 text-small">
                      {vehicleAccessories.map((item: any, index: number) => (
                        <CardEntry
                          title={item.name}
                          value={formatCurrency(item.amount)}
                          id={'novatedleasesection.dealeraccessory_' + index}
                        />
                      ))}
                    </div>
                  </div>
                )}

              {aftermarketOrderLines && aftermarketOrderLines.length > 0 && (
                <div className="mt-3">
                  <SBText theme={theme} size={18}>
                    Arranged by {globals.brand}
                  </SBText>
                  <div className="ml-3 text-small">
                    {aftermarketOrderLines.map((item: any, index: number) => (
                      <CardEntry
                        title={item.description}
                        value={formatCurrency(item.priceIncGST)}
                        id={'novatedleasesection.maxxiaaccessory_' + index}
                      />
                    ))}
                  </div>
                </div>
              )}
              {accessoriesByYou && accessoriesByYou.length > 0 && (
                <div className="mt-3">
                  <SBText size={18} theme={theme}>
                    Arranged by you
                  </SBText>
                  <div className="ml-3 text-small">
                    {accessoriesByYou.map((item: any, index: number) => (
                      <CardEntry
                        title={item.description}
                        value={formatCurrency(item.amountIncGST)}
                        id={'novatedleasesection.selfaccessory_' + index}
                      />
                    ))}
                  </div>
                  <hr></hr>
                </div>
              )}
            </div>
          )}

          {(IsSpecialEmployer('NSWHN') || IsSpecialEmployer('SAGBYO')) && (props.data.vehicle.registrationFee > 0 || props.data.vehicle.ctp > 0 ||  props.data.vehicle.stampDuty > 0) && (
            <FeeDetail theme={theme}>
              <Row>
                <Col>'Car price' includes the below on-road charges:</Col>
              </Row>
              <CardEntry
                title="Registration"
                id="financialinclusionsection.registrationfee"
                value={formatCurrency(props.data.vehicle.registrationFee)}
              />
              <CardEntry
                title="Compulsory Third Party"
                id="financialinclusionsection.ctp"
                value={formatCurrency(props.data.vehicle.ctp)}
              />
              <CardEntry
                title="Stamp Duty"
                id="financialinclusionsection.stampduty"
                value={formatCurrency(props.data.vehicle.stampDuty)}
              />
            </FeeDetail>
          )}

          {props.data.gstCreditClaimable > 0 && (
            <CardEntry
              title={
                globals.isBrandMX
                  ? 'Upfront GST savings'
                  : 'Less estimated GST savings on vehicle'
              }
              id="novatedleasesection.gstcredit"
              value={'-' + formatCurrency(props.data.gstCreditClaimable)}
              boldText
              boldValue={globals.isBrandRS}
              valueColour={theme.colors.positive}
            />
          )}
          <CardEntry
            title="Stamp duty via disbursement"
            value={formatCurrency(props.data.vehicle.stampDutyViaDisbursement)}
            id="novatedleasesection.stampduty"
            boldText={globals.isBrandRS}
          />
          <CardEntry
            title="Negative equity"
            value={formatCurrency(props.data.negativeEquityAmount)}
            id="novatedleasesection.negativeequityamount"
            boldText={globals.isBrandRS}
          />
          <CardEntry
            title={
              globals.isBrandMX
                ? "Financier's Establishment Fee"
                : 'Finance Establishment Fee'
            }
            value={formatCurrency(props.data.financierEstablishmentFee)}
            id="novatedleasesection.financierestablishmentfee"
            boldText={globals.isBrandRS}
          />
          {props.data.compInsurancePremiumFinanced && props.data.compInsurancePremium > 0 && (
            <div>
              <CardEntry
                title={'1st year Comprehensive Insurance' }
                value={formatCurrency(props.data.compInsurancePremium)}
                id="novatedleasesection.compinsurancepremium"
                boldText={globals.isBrandRS}
              />
              {props.isProposal && (
            <S.BrandDefaultText
              className="text-smaller mb-3"
              id="novatedleasesection.compinsdescription"
            >
              {props.data.compInsuranceProvider === 'Allianz'
                ? ' (' + globals.brand +' arranged, you have the choice of repairer. Please refer to your PDS for details about choice of repairer.)'
                : ' (' + globals.brand +' arranged)'
              }
            </S.BrandDefaultText>
          )}
            </div>

          )}
          {/* Continue to show insurance info on the same card if not a pdf, or is a pdf but 
              not enough accessories to overflow page  */}
          {(!isDownloadingPdf ||
            (isDownloadingPdf && accessoryCount < 5) ||
            (isEVRebate && accessoryCount < 3)) && (
            <div className="mt-3">
              <InsuranceInfoSection />
            </div>
          )}
        </div>
      </Card.Body>
    )
  }

  return (
    <div
      className="card-section print-page-break-before"
      id="novatedleasesection"
    >
      <Container className="section mt-4">
        {!props.data.pdf && (
          <Card
            className={
              props.data.pdf
                ? 'mb-13rem pdf-card-container'
                : 'mb-13rem card-shadow'
            }
          >
            <Card.Header
              className={
                props.data.pdf ? 'border-bottom-0 pdf-card' : 'border-bottom-0'
              }
            >
              <S.CardHeader
                className="text-header"
                id="novatedleasesection.title"
              >
                {props.title}
              </S.CardHeader>
            </Card.Header>
            {CardBody(theme)}
          </Card>
        )}

        {props.data.pdf && (
          <P.StyledCard>
            <P.StyledCardHeader>
              <P.StyledCardHeaderText id="cardetailssection.title">
                {props.title}
              </P.StyledCardHeaderText>
            </P.StyledCardHeader>
            {CardBody(theme)}
          </P.StyledCard>
        )}
      </Container>

      {/* If too many accessories on pdf to fit on one page, split to second overflow page  */}
      {isDownloadingPdf &&
        (accessoryCount > 4 || (isEVRebate && accessoryCount > 2)) && (
          <Container className="section mt-4">
            <P.StyledCard className='mb-13rem pdf-card-container'>
              <P.StyledCardHeader>
                <P.StyledCardHeaderText id="cardetailssection.title">
                  {props.title + ' continued'}
                </P.StyledCardHeaderText>
              </P.StyledCardHeader>
              <Card.Body>
                <div>
                  <InsuranceInfoSection />
                </div>
              </Card.Body>
            </P.StyledCard>
          </Container>
        )}
    </div>
  )
}

export default NovatedLeaseSection
